/*
----------------------------------------------------------------------------
Usage:
----------------------------------------------------------------------------
yarn add @glidejs/glide
import Glider from './modules/glider'
_('glider').nodes().map((el) => new Glider(el))

@import "node_modules/@glidejs/glide/src/assets/sass/glide.core"

----------------------------------------------------------------------------
Attributes:
----------------------------------------------------------------------------
data-per-view="5,4,2,1"             - large, medium, small, column count
data-bullets="0,1,1,1"              - large, medium, small, 0 false or 1 true
data-element="bullets-container"    - where the bullets will go
data-gap                            - space between items
data-glideautoplay                  - time in seconds (will be converted to milliseconds)
*/
import { _ } from './../utils.js'
import config from '../config/main.js'
import Glide, {
    Controls,
    Swipe,
    Keyboard,
    Images,
    Breakpoints,
    Autoplay,
    Anchors,
} from '@glidejs/glide/dist/glide.modular.esm.js'

export default class Glider {
    constructor(el) {
        this.el = el
        this.showBullets = this.el.getAttribute('data-bullets')
            ? this.el.getAttribute('data-bullets').split(',')
            : false
        this.showArrows = this.el.getAttribute('data-arrows')
            ? this.el.getAttribute('data-arrows').split(',')
            : false
        this.glideautoplay = this.el.getAttribute('data-glideautoplay')
            ? this.el.getAttribute('data-glideautoplay').split(',')
            : false
        this.slides = _('slide', 'element').nodesFrom(this.el)
        this.slideCount = this.slides.length

        this.xlargeBreak = window.matchMedia(
            `(max-width: ${config.breakpoint.xlarge}px)`,
        )

        this.largeBreak = window.matchMedia(
            `(max-width: ${config.breakpoint.large}px)`,
        )

        this.mediumBreak = window.matchMedia(
            `(max-width: ${config.breakpoint.medium}px)`,
        )

        this.smallBreak = window.matchMedia(
            `(max-width: ${config.breakpoint.small}px)`,
        )

        this.bulletsContainer = _('bullets-container', 'element').nodeFrom(
            this.el,
        )

        this.infiniteScrollArrows = _(
            'infinite-scroll-arrows',
            'element',
        ).nodeFrom(this.el)

        this.viewCount = this.el.getAttribute('data-per-view')
            ? this.el.getAttribute('data-per-view').split(',')
            : false

        this.peekAfter = 0
        if (
            this.el.getAttribute('data-peek-after') &&
            this.smallBreak.matches
        ) {
            this.peekAfter = 70
        }
        this.type = this.el.getAttribute('data-type')
            ? this.el.getAttribute('data-type')
            : 'carousel'

        if (this.glideautoplay) {
            this.transitionTime = this.glideautoplay * 1000
        } else {
            this.transitionTime = false
        }

        this.previousArrow = _('<', 'glide-dir').nodeFrom(this.el)
        this.nextArrow = _('>', 'glide-dir').nodeFrom(this.el)
        this.glide = new Glide(this.el, {
            autoplay: this.transitionTime,
            hoverpause: true,
            perTouch: 1,
            perView: this.viewCount[0] || 4,
            rewindDuration: 0,
            type: this.type,
            swipeThreshold: 10,
            startAt: 0,
            rewind: false,
            gap: this.el.dataset.gap || 20,
            peek: {
                before: 0,
                after: this.peekAfter,
            },
            breakpoints: {
                1280: {
                    perView: this.viewCount[1] || 3,
                },
                1024: {
                    perView: this.viewCount[1] || 3,
                },
                768: {
                    perView: this.viewCount[2] || 2,
                },
                568: {
                    perView: this.viewCount[3] || 1,
                },
            },
            anchors: {
                throttle: 200,
            },
        })

        if (this.slideCount <= this.viewCount[0]) {
            if (this.previousArrow) {
                this.hideControls()
            }
        }

        this.showHideControls()
        this.smallBreak.addListener(this.showHideControls)
        this.mediumBreak.addListener(this.showHideControls)
        this.largeBreak.addListener(this.showHideControls)
        this.xlargeBreak.addListener(this.showHideControls)
        this.glide.on(['mount.before', 'run'], this.showHideArrows)
        this.glide.on('mount.after', () => {
            document.querySelectorAll('.glide__slide--clone h1').forEach(h1 => {
                // Replace the cloned <h1> with <h2>
                const replacement = document.createElement('h2')
                replacement.innerHTML = h1.innerHTML;

                // Copy over attributes if needed
                [...h1.attributes].forEach(attr => {
                    replacement.setAttribute(attr.name, attr.value)
                })

                h1.replaceWith(replacement)
            })
        })
        this.glide.mount({
            Controls,
            Swipe,
            Keyboard,
            Images,
            Breakpoints,
            Autoplay,
            Anchors,
        })
        this.glide.on(['mount.before', 'run'], this.removeArrowDisabled)
    }

    checkScreenSize = () => {
        let size = 0
        if (this.xlargeBreak.matches) {
            size = 1
        }
        if (this.largeBreak.matches) {
            size = 1
        }
        if (this.mediumBreak.matches) {
            size = 2
        }
        if (this.smallBreak.matches) {
            size = 3
        }
        return size
    }

    showHideControls = () => {
        const slidesPerView =
            this.slideCount / this.viewCount[this.checkScreenSize()]
        if (slidesPerView <= 1) {
            this.glide.disable()
            this.bulletsContainer.innerHTML = ''
            this.el.classList.remove('glide--active')
            return false
        }
        this.glide.enable()
        this.bulletsContainer.innerHTML = ''
        this.el.classList.add('glide--active')
        if (this.showBullets[this.checkScreenSize()] === '1') {
            for (let index = 0; index < this.slideCount; index++) {
                const button = document.createElement('button')
                button.className = 'glide__bullet'
                button.setAttribute('aria-label', 'Slide ' + (index + 1))
                button.setAttribute('data-glide-dir', '=' + index)
                this.bulletsContainer.appendChild(button)
            }
        }
    }

    showHideArrows = () => {
        if (!this.showArrows) {
            this.arrowDisplay()
        } else {
            if (
                this.showArrows &&
                this.showArrows[this.checkScreenSize()] === '1'
            ) {
                this.arrowDisplay()
            } else {
                this.previousArrow.classList.add('u-hidden')
                this.nextArrow.classList.add('u-hidden')
            }
        }
    }

    arrowDisplay = () => {
        if (this.previousArrow) {
            if (this.glide.index === 0) {
                this.previousArrow.classList.add('glide__arrow--disabled')
            } else {
                this.previousArrow.classList.remove('glide__arrow--disabled')
            }
        }
        if (this.nextArrow) {
            if (this.glide.index === this.slideCount - 1) {
                this.nextArrow.classList.add('glide__arrow--disabled')
            } else {
                this.nextArrow.classList.remove('glide__arrow--disabled')
            }
        }
    }

    removeArrowDisabled = () => {
        if (this.infiniteScrollArrows) {
            if (
                (this.checkScreenSize() === 0 && this.previousArrow) ||
                this.nextArrow
            ) {
                this.previousArrow.classList.remove('glide__arrow--disabled')
                this.nextArrow.classList.remove('glide__arrow--disabled')
            }
        }
    }

    hideControls = () => {
        this.previousArrow.classList.add('u-hidden')
        this.nextArrow.classList.add('u-hidden')
    }
}